import {
  createRng,
  IllegalMoveError,
  type GameEngine,
  type GameOptions,
  type RNG,
  type ScoreResult,
  type Seat,
} from "@bing/engine-core";
import {
  applyStaged,
  boardFromState,
  canBearOff,
  diceRemaining,
  expandRoll,
  frozenPoints,
  noRollCanPlay,
  legalNextSubMoves,
  magnitudeOf,
  pipCount,
  pipToOff,
  setupBoard,
  subMoveEq,
  type SubMove,
} from "./rules";
import {
  type BgColor,
  type BgConfig,
  type BgMove,
  type BgOpeningRoll,
  type BgRoundResult,
  type BgSpot,
  type BgState,
  type BgView,
  BG_MATCH_TARGETS,
  DEFAULT_BG_MATCH_TARGET,
} from "./types";

/** Backgammon is 2-player: seat 0 / seat 1 (colour-neutral). */
export const BACKGAMMON_SEATS = 2;

function configFrom(opts: GameOptions): BgConfig {
  const r = (opts.rules ?? {}) as Record<string, unknown>;
  return {
    noHitAndRunInHome:
      r["noHitAndRunInHome"] === undefined ? true : Boolean(r["noHitAndRunInHome"]),
    mustBearOffWhenPossible: Boolean(r["mustBearOffWhenPossible"]),
    // سگ‌مارس is ON by default (the traditional game scores it 3); an explicit `false` turns it off.
    countSagMars: r["countSagMars"] === undefined ? true : Boolean(r["countSagMars"]),
  };
}

/** Points needed to win the match. Client-supplied, so it is validated against the allowed set. */
function matchTargetFrom(opts: GameOptions): number {
  const r = (opts.rules ?? {}) as Record<string, unknown>;
  const v = r["matchTo"];
  return BG_MATCH_TARGETS.includes(v as never) ? (v as number) : DEFAULT_BG_MATCH_TARGET;
}

/** Disambiguate the die for a client `{from,to}` sub-move (ambiguous only when bearing off). */
function resolveMove(
  next: readonly SubMove[],
  seat: BgColor,
  from: BgSpot,
  to: BgSpot,
): SubMove | null {
  const matches = next.filter((s) => s.from === from && s.to === to);
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0]!;
  // Bearing off from the same point with two dice: prefer the exact-pip die, else the smallest.
  if (to === "off" && typeof from === "number") {
    const exact = pipToOff(seat, from);
    return (
      matches.find((s) => s.die === exact) ?? matches.reduce((a, b) => (a.die <= b.die ? a : b))
    );
  }
  return matches[0]!;
}

/**
 * How long a landed roll is left on the table before the bot plays it: the client's throw
 * (`DIE_THROW_MS` = 1000ms) PLUS 1600ms to actually read the numbers.
 *
 * Raised from 2200 for the «deliberate» tempo. **LOCKSTEP** with `THROW_MS + ROLL_HOLD_MS` in
 * `apps/web/src/game/backgammon-replay.ts` — the client paces a HUMAN opponent to the same beat, so
 * change one and you must change the other or a bot's roll and a person's roll will feel different.
 */
export const ROLL_READ_MS = 2600;

/**
 * How long a won دست stays on screen before the next one is dealt — long enough to read the result
 * banner and the new score. **LOCKSTEP** with `ROUND_HOLD_MS` in apps/web/src/screens/
 * BackgammonTable.tsx, which auto-deals the next round on the same beat.
 */
export const ROUND_HOLD_MS = 3400;

/**
 * ⚠️ LEGACY SOLO SAVES. `matchTo`/`score`/`round`/`pendingRound` arrived on 2026-08-14; a solo
 * `ActiveGame` row persisted before that has NONE of them. Reading them raw made a resume CRASH:
 * `undefined !== null` is true, so the only legal move became `nextRound`, whose branch then did
 * `undefined + 1` and spread an undefined `score` («Cannot read properties of undefined»). Every
 * read goes through here instead — the same shape as hearts' `completedTrick ?? []` fix.
 *
 * The defaults restore the game the row was SAVED as: a single دست at 0–0, round 1, nothing held.
 * (Online is unaffected either way — backgammon is not in `RESUMABLE_ONLINE_GAMES`.)
 */
function matchOf(state: BgState): {
  matchTo: number;
  score: [number, number];
  round: number;
  pendingRound: BgRoundResult | null;
  /** Null unless a §5.1 contest is live. A legacy row has no such field and is PAST its opening. */
  openingRoll: BgOpeningRoll | null;
} {
  const score = state.score ?? [0, 0];
  return {
    matchTo: state.matchTo ?? 1,
    score: [score[0] ?? 0, score[1] ?? 0],
    round: state.round ?? 1,
    pendingRound: state.pendingRound ?? null,
    openingRoll: state.openingRoll ?? null,
  };
}

/** A fresh §5.1 contest: nobody has thrown yet. */
const NEW_CONTEST: BgOpeningRoll = { dice: [null, null], ties: 0 };

/**
 * How long the decided contest is left on the table before play starts — the window in which the
 * client announces «شروع با تو / با حریف» over the two dice. LOCKSTEP with `OPENING_MS` in
 * apps/web/src/game/backgammon-replay.ts, which is the client's own hold for that announcement.
 */
export const OPENING_ANNOUNCE_MS = 3000;

/**
 * How long «حرکتی نداری» is read before a turn with no move passes on its own — the length the
 * board's blocked notice has always run (the web's `AUTO_CONFIRM_MS`, which it now imports from
 * here, so the two cannot drift). card-98 item 6 (operator, 2026-10-27): «the usual notice … and
 * the turn passes on its own».
 */
export const NO_MOVE_NOTICE_MS = 2000;

/**
 * THE TURN NO ROLL CAN PLAY (card-98 item 6; the operator's 6.3, 2026-10-28) — before the roll, not
 * one die can move anything: CLOSED OUT on the bar, or a FULL PRIME with nothing on the bar. The
 * ROOM passes it on its own clock (`systemStepDelayMs` / `applySystemStep`) and the seat is offered
 * nothing — no roll, no confirm.
 *
 * A DANCED ROLL is deliberately NOT here (operator 6.2: «keep the old pace — a no-move turn (bot,
 * idle player or reduced motion) takes no longer than before»). It keeps 4ef63408's flow: `confirm`
 * is its only move, made by a bot or an expired turn's auto-play on their own beat
 * (`postMoveDelayMs`) and by a person's board silently once the notice has been read. One table
 * timer could not match those three old beats. Nor is a turn that STAGED something and cannot go
 * on: confirming that is the player's decision (they may undo).
 *
 * MEMOISED PER STATE OBJECT (review C-M6): a room frame asks it from `legalMoves`, `isLegal`,
 * `viewFor` and `systemStepDelayMs` several times over. A state is immutable — this engine never
 * mutates one, every transition returns a new object — so the answer for an object never changes,
 * and a `WeakMap` keeps nothing alive.
 */
const noRollMemo = new WeakMap<BgState, boolean>();

function noRollTurn(state: BgState): boolean {
  const known = noRollMemo.get(state);
  if (known !== undefined) return known;
  const answer = computeNoRoll(state);
  noRollMemo.set(state, answer);
  return answer;
}

function computeNoRoll(state: BgState): boolean {
  if (state.winner !== null) return false;
  const m = matchOf(state);
  if (m.openingRoll !== null || m.pendingRound !== null) return false;
  if (state.dice.length > 0) return false;
  return noRollCanPlay(boardFromState(state), state.turn, state.config);
}

export const backgammonEngine: GameEngine<BgState, BgMove, BgView> = {
  gameId: "backgammon",

  createInitialState(opts: GameOptions, rng: RNG): BgState {
    const board = setupBoard();
    /*
     * ⚠️ THE OPENING IS NOT DECIDED HERE ANY MORE. `resolveOpeningRoll` used to throw BOTH dice
     * inside this call, so the roll that decides who starts was made for the player before they
     * ever saw the board — «the game doesn't wait for me to throw the opening die». The game now
     * opens ON the contest: each seat throws its own die with `{kind:"openingRoll"}`.
     */
    return {
      points: board.points,
      bar: board.bar,
      off: board.off,
      turn: 0, // a placeholder — the contest decides it, and nothing may act until it does
      dice: [],
      staged: [],
      rngState: rng.getState(),
      variant: "takhteh",
      config: configFrom(opts),
      winner: null,
      matchTo: matchTargetFrom(opts),
      score: [0, 0],
      round: 1,
      pendingRound: null,
      lastTurn: [],
      openingRoll: NEW_CONTEST,
    };
  },

  legalMoves(state: BgState, seat: Seat): BgMove[] {
    if (state.winner !== null) return [];
    /*
     * §5.1 — THE CONTEST. Both seats may act, each throwing their OWN die, in either order: this is
     * the game's one SIMULTANEOUS phase (Hearts' passing is the other in this codebase). A seat
     * that has already thrown has nothing to do but wait.
     */
    const contest = matchOf(state).openingRoll;
    if (contest !== null) {
      return contest.dice[seat === 0 ? 0 : 1] === null ? [{ kind: "openingRoll" }] : [];
    }
    // A دست has been won and the match goes on: the board holds that final position until somebody
    // deals the next round. Offered to BOTH seats so either side (or a bot) can unstick it.
    if (matchOf(state).pendingRound !== null) return [{ kind: "nextRound" }];
    if (seat !== state.turn) return [];
    // card-98 item 6 — a turn NO roll can play is not rolled: the room passes it.
    if (noRollTurn(state)) return [];
    if (state.dice.length === 0) return [{ kind: "roll" }];
    const next = legalNextSubMoves(
      boardFromState(state),
      state.turn,
      state.dice,
      state.staged,
      state.config,
    );
    if (next.length > 0) return next.map((s) => ({ kind: "move", from: s.from, to: s.to }));
    return [{ kind: "confirm" }]; // a maximal turn is staged — confirming it is the player's call
  },

  isLegal(state: BgState, seat: Seat, move: BgMove): boolean {
    if (move.kind === "double" || move.kind === "take" || move.kind === "drop") return false; // cube off in v1
    if (state.winner !== null) return false;
    const contest = matchOf(state).openingRoll;
    if (contest !== null) {
      // Nothing but your own contest die, and only once.
      return move.kind === "openingRoll" && contest.dice[seat === 0 ? 0 : 1] === null;
    }
    if (move.kind === "openingRoll") return false;
    if (matchOf(state).pendingRound !== null) return move.kind === "nextRound";
    if (move.kind === "nextRound") return false;
    if (seat !== state.turn) return false;
    // No roll can play: nothing is legal — the room passes the turn.
    if (noRollTurn(state)) return false;
    if (move.kind === "roll") return state.dice.length === 0;
    if (move.kind === "undo") return state.staged.length > 0;
    if (state.dice.length === 0) return false;
    const next = legalNextSubMoves(
      boardFromState(state),
      state.turn,
      state.dice,
      state.staged,
      state.config,
    );
    if (move.kind === "confirm") return next.length === 0;
    return next.some((s) => s.from === move.from && s.to === move.to);
  },

  applyMove(state: BgState, seat: Seat, move: BgMove): BgState {
    if (!backgammonEngine.isLegal(state, seat, move)) throw new IllegalMoveError(seat, move);
    if (move.kind === "openingRoll") {
      /*
       * ONE die, for this seat only. When the second one lands the contest is decided: the higher
       * number starts and THE TWO CONTEST DICE ARE THEIR FIRST TURN — no re-roll (the Iranian rule:
       * «شخص آغازکننده با همان تاس ریخته شده بازی می‌کند»). A TIE is simply thrown again, which is
       * why the deciding pair can never be equal and the opening turn is never a double.
       */
      const rng = createRng(state.rngState);
      const contest = matchOf(state).openingRoll ?? NEW_CONTEST;
      const me = seat === 0 ? 0 : 1;
      const rolled = rng.range(1, 6);
      const dice: [number | null, number | null] = [
        contest.dice[0] ?? null,
        contest.dice[1] ?? null,
      ];
      dice[me] = rolled;
      const [a, b] = dice;
      if (a === null || b === null) {
        return { ...state, openingRoll: { dice, ties: contest.ties }, rngState: rng.getState() };
      }
      if (a === b) {
        // A tie: both dice go back on the table and the contest is thrown again — but the PAIR is
        // held for display (`tied`), because this is the only moment the second die exists at all
        // and without it the thrower watches their own throw vanish under the word «مساوی».
        return {
          ...state,
          openingRoll: { dice: [null, null], ties: contest.ties + 1, tied: [a, b] },
          rngState: rng.getState(),
        };
      }
      return {
        ...state,
        turn: a > b ? 0 : 1,
        // The starter's own die first, the opponent's second — the pair they actually play.
        dice: a > b ? expandRoll(a, b) : expandRoll(b, a),
        staged: [],
        opening: [a, b],
        openingRoll: null,
        rngState: rng.getState(),
      };
    }
    if (move.kind === "roll") {
      const rng = createRng(state.rngState);
      const a = rng.range(1, 6);
      const b = rng.range(1, 6);
      return { ...state, dice: expandRoll(a, b), staged: [], rngState: rng.getState() };
    }
    if (move.kind === "nextRound") {
      // A fresh دست: a new board and a NEW opening contest — EACH round is decided on its own roll,
      // and each is thrown by the players. The running score and the target carry over untouched.
      const board = setupBoard();
      return {
        ...state,
        points: board.points,
        bar: board.bar,
        off: board.off,
        turn: 0, // a placeholder again — the new contest decides it
        dice: [],
        staged: [],
        lastTurn: [],
        // Dropped so nothing announces the PREVIOUS دست's contest over the new board.
        opening: undefined,
        openingRoll: NEW_CONTEST,
        round: matchOf(state).round + 1,
        pendingRound: null,
      };
    }
    if (move.kind === "undo") return { ...state, staged: state.staged.slice(0, -1) };
    if (move.kind === "move") {
      const next = legalNextSubMoves(
        boardFromState(state),
        state.turn,
        state.dice,
        state.staged,
        state.config,
      );
      const sub = resolveMove(next, state.turn, move.from, move.to);
      if (!sub) throw new IllegalMoveError(seat, move, "no matching legal sub-move");
      return { ...state, staged: [...state.staged, sub] };
    }
    // confirm: commit staged into the board, score a finished دست, flip the turn.
    const result = applyStaged(boardFromState(state), state.turn, state.staged);
    const off15 = result.off[state.turn] === 15;
    const lastTurn = state.staged.map((s) => ({ from: s.from, to: s.to }));
    if (!off15) {
      return {
        ...state,
        points: result.points,
        bar: result.bar,
        off: result.off,
        turn: state.turn === 0 ? 1 : 0,
        dice: [],
        staged: [],
        lastTurn,
      };
    }
    /*
     * THE دست IS WON. Its points go on the board — عادی 1, مارس 2, سگ‌مارس 3 — and the MATCH ends
     * only if that gets the winner to the target. Otherwise the position is held (`pendingRound`)
     * so both players see how it finished, and the next round is dealt by `nextRound`.
     */
    const roundWinner = state.turn;
    const magnitude = magnitudeOf(result, roundWinner, state.config);
    const { matchTo, score } = matchOf(state);
    score[roundWinner] += magnitude;
    const matchOver = score[roundWinner] >= matchTo;
    return {
      ...state,
      points: result.points,
      bar: result.bar,
      off: result.off,
      turn: roundWinner,
      dice: [],
      staged: [],
      score,
      winner: matchOver ? roundWinner : null,
      pendingRound: matchOver ? null : { winner: roundWinner, magnitude },
      lastTurn,
    };
  },

  /** The MATCH — not a single دست. A round win only ends things if its points reach the target. */
  isTerminal(state: BgState): boolean {
    return state.winner !== null;
  },

  /** The MATCH result: the accumulated points, and the side that reached the target. */
  score(state: BgState): ScoreResult {
    const winner = state.winner;
    if (winner === null) return { seatScores: [0, 0], winners: [] }; // room-decided ends don't call this
    const magnitude = magnitudeOf(boardFromState(state), winner, state.config);
    return {
      seatScores: [...matchOf(state).score],
      winners: [winner],
      // `magnitude` is the FINAL دست's, which is what the end card announces («مارس بردی!»).
      detail: { magnitude, matchTo: matchOf(state).matchTo, round: matchOf(state).round },
    };
  },

  viewFor(state: BgState, seat: Seat): BgView {
    const committed = boardFromState(state);
    const display = applyStaged(committed, state.turn, state.staged);
    // Nothing on the board is actionable while the §5.1 contest is running — not even for the seat
    // `turn` happens to point at, which is a placeholder until the contest decides it.
    const contest = matchOf(state).openingRoll;
    const isMover = seat === state.turn && state.winner === null && contest === null;
    let legalFrom: BgView["legalFrom"] = [];
    const legalTo: Record<string, BgView["legalTo"][string]> = {};
    let mustConfirm = false;
    let noLegalMove = false;
    // §5.2 — asked of the ENGINE, and only for the seat on turn (a viewer is never being restricted).
    const fz =
      isMover && state.dice.length > 0
        ? frozenPoints(committed, state.turn, state.dice, state.staged, state.config)
        : { locked: [], lifted: [] };
    // card-98 item 6 — no roll can play (no dice): «حرکتی نداری» for the MOVER only, and the room
    // passes the turn on its own clock. A danced roll says the same below, as it did at 4ef63408.
    if (isMover && noRollTurn(state)) noLegalMove = true;
    if (isMover && state.dice.length > 0) {
      const next = legalNextSubMoves(committed, state.turn, state.dice, state.staged, state.config);
      if (next.length === 0) {
        // 4ef63408's flow, unchanged (operator 6.2): `confirm` is the only move — a staged maximal
        // turn's «تأیید», or the end of a dance (`noLegalMove`), which the board sends by itself.
        mustConfirm = true;
        noLegalMove = state.staged.length === 0;
      } else {
        const froms: BgSpot[] = [];
        for (const s of next) {
          if (!froms.includes(s.from)) froms.push(s.from);
          const key = String(s.from);
          const tos = (legalTo[key] ??= []) as BgSpot[];
          if (!tos.includes(s.to)) tos.push(s.to);
        }
        legalFrom = froms;
      }
    }
    // ⚠️ «over» now means the MATCH is over. A won دست inside a live match keeps the board in
    // «playing» and reports itself through `roundResult`, so the end card does not fire per round.
    const matchOver = state.winner !== null;
    const winnerSeat: BgColor | undefined = state.winner ?? undefined;
    const m = matchOf(state);
    return {
      you: seat,
      turn: state.turn,
      points: display.points,
      bar: display.bar,
      off: display.off,
      dice: [...state.dice],
      diceRemaining: diceRemaining(state.dice, state.staged),
      isDoubles: state.dice.length === 4,
      staged: state.staged.map((s) => ({ from: s.from, to: s.to })),
      legalFrom,
      legalTo,
      ...(fz.locked.length > 0 ? { frozen: fz.locked } : {}),
      ...(fz.lifted.length > 0 ? { frozenLifted: fz.lifted } : {}),
      mustEnterFromBar: isMover && state.dice.length > 0 && display.bar[state.turn] > 0,
      canBearOff: isMover && canBearOff(display, state.turn),
      mustConfirm,
      noLegalMove,
      lastMove: state.lastTurn.map((sm) => ({ from: sm.from, to: sm.to })),
      ...(state.opening !== undefined ? { opening: state.opening } : {}),
      pip: [pipCount(display, 0), pipCount(display, 1)],
      matchTo: m.matchTo,
      score: [...m.score],
      round: m.round,
      ...(m.pendingRound !== null ? { roundResult: m.pendingRound } : {}),
      ...(contest !== null ? { openingRoll: contest } : {}),
      status: matchOver ? "over" : "playing",
      ...(winnerSeat !== undefined ? { winner: winnerSeat } : {}),
      ...(winnerSeat !== undefined
        ? { magnitude: magnitudeOf(display, winnerSeat, state.config) }
        : {}),
    };
  },

  /** Pace the solo bot's roll (dice tumble) + each slide, so the client animates them (Hokm's idiom). */
  postMoveDelayMs(state: BgState): number | undefined {
    // A finished دست is HELD so both players can read how it ended (and what it scored) before the
    // next board appears. This is also the beat a bot waits before dealing the next round.
    if (matchOf(state).pendingRound !== null) return ROUND_HOLD_MS;
    if (state.winner !== null) return undefined;
    /*
     * §5.1 — pace the contest. A bot must not slap its die down the instant the human throws (or,
     * worse, throw first and have the whole thing decided before the player has touched anything):
     * its throw is worth the same read window as any other roll. And once the contest is DECIDED,
     * hold again so «شروع با تو / با حریف» can be read over the two dice before play starts.
     */
    if (matchOf(state).openingRoll !== null) return ROLL_READ_MS;
    // …the FIRST turn of the دست only (`lastTurn` is empty until something has been confirmed in
    // it), or every later roll would inherit the announcement's longer hold.
    if (
      state.opening !== undefined &&
      state.lastTurn.length === 0 &&
      state.dice.length > 0 &&
      state.staged.length === 0
    ) {
      return OPENING_ANNOUNCE_MS;
    }
    // Dice just landed (the bot rolled, nothing staged yet): HOLD long enough for the client's
    // throw animation (DIE_THROW_MS = 1000ms) to finish AND leave real time to read the numbers
    // before the bot starts moving. (منچ has its own, shorter `ROLL_READ_MS` of 2200 — تخته نرد
    // deliberately runs the slower «deliberate» tempo, so the two are NO LONGER the same number.)
    // Shorten it and a bot's roll stops being watchable.
    if (state.dice.length > 0 && state.staged.length === 0) return ROLL_READ_MS;
    // Between the bot's sub-moves: slow enough that a human can WATCH each حریف checker move (was 250ms
    // = a blur, then 750ms). **LOCKSTEP** with `STEP_MS` in apps/web/src/game/backgammon-replay.ts,
    // which paces a human opponent's sub-moves to the same beat.
    if (state.dice.length > 0) return 950;
    return undefined; // pre-roll: normal cadence
  },

  /**
   * card-98 item 6 — THE PASS OF A TURN NO ROLL CAN PLAY, on the room's own clock: the notice alone
   * ({@link NO_MOVE_NOTICE_MS}) — nothing is thrown. While it is pending the room plays no bot and
   * runs no turn window (the step owns the clock), so a seat that cannot move is never charged an
   * idle miss for it. Every path through it is shorter than 4ef63408's roll-then-dance (operator
   * 6.2). A DANCED roll is not a step: it ends on its old beat (see {@link noRollTurn}).
   */
  systemStepDelayMs(state: BgState): number | undefined {
    return noRollTurn(state) ? NO_MOVE_NOTICE_MS : undefined;
  },

  /**
   * Pass the turn: the other seat is on turn, pre-roll. Nothing is drawn from the RNG (no die is
   * thrown for a pass) and `lastTurn` is EMPTY — nothing was played, so the board has no trail to
   * draw and the replay nothing to walk. The same transition a danced `confirm` makes.
   */
  applySystemStep(state: BgState): BgState {
    if (!noRollTurn(state)) return state;
    return {
      ...state,
      turn: state.turn === 0 ? 1 : 0,
      dice: [],
      staged: [],
      lastTurn: [],
    };
  },
};

// re-export so `import { subMoveEq } from "./engine"` in tests/tools stays available if needed
export { subMoveEq };
